import React from 'react';

/**
 * Whether this account's phone number has ever been proven by SMS.
 *
 * ## Why this is a separate chip from "Verified"
 *
 * The console already shows a status that reads "Not verified" on a provider,
 * and it means something else entirely: their DOCUMENTS have not been approved
 * by an administrator. One word was covering two unrelated facts, and the more
 * consequential of the two was invisible.
 *
 * Until 2026-09-04 sign-up wrote `isVerify: true` unconditionally, so not one
 * phone number in this database had ever been checked. Production still holds
 * `4555556` -- seven digits, not a phone. In a product whose whole job is
 * getting a clinician to a patient, the number is the only way to call someone
 * back, and "we have never confirmed anybody can be reached on this" is worth
 * its own column.
 *
 * ## The wording
 *
 * "Phone confirmed" / "Phone unconfirmed", never "verified", precisely so the
 * two chips cannot be read as the same thing at a glance. Support reads these
 * columns under time pressure.
 */
export const PhoneVerified = ({
  verifiedAt,
  style,
}: {
  verifiedAt?: string | Date | null;
  style?: React.CSSProperties;
}) => {
  const confirmed = Boolean(verifiedAt);

  return (
    <span
      data-testid="phone-verified"
      data-confirmed={confirmed ? 'true' : 'false'}
      title={
        confirmed
          ? `Confirmed by SMS on ${new Date(verifiedAt as string).toLocaleDateString()}`
          : 'This number has never been proven. The account is asked for a code the next time it signs in.'
      }
      style={{
        color: confirmed ? '#4BA16E' : '#9A6700',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {confirmed ? 'Phone confirmed' : 'Phone unconfirmed'}
    </span>
  );
};
